/**
 * The business clock: dates and times as they stand in Vancouver, wherever the reader (or the
 * server) is. "Today" on a roadmap, the day a project was banked and the time on a comment are
 * facts about the business, so someone in Auckland and someone in London must see the same ones;
 * each reader's own timezone would put them a day apart at the edges. The company roadmap made
 * the same call (`ROADMAP_TIME_ZONE` in packages/roadmap/src/quarter.ts).
 *
 * Pure and isomorphic, with no React: servers import it as `@noissue/os-ui/lib/business-time`.
 */
import { MONTHS } from './iso-date';

/** Changing it moves every "today" and every stamp, so it is a business decision, not config. */
export const BUSINESS_TIME_ZONE = 'America/Vancouver';

const CLOCK = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export interface BusinessClock {
  year: number;
  /** 1–12. */
  month: number;
  day: number;
  hours: number;
  minutes: number;
}

/** An instant's wall-clock reading in Vancouver. */
export function businessClock(instant: Date | string): BusinessClock {
  const parts = Object.fromEntries(
    CLOCK.formatToParts(typeof instant === 'string' ? new Date(instant) : instant).map(
      (part) => [part.type, Number(part.value)],
    ),
  );
  return {
    year: parts.year!,
    month: parts.month!,
    day: parts.day!,
    hours: parts.hour!,
    minutes: parts.minute!,
  };
}

/** "12 Oct, 14:05": when something was said or added, on the business clock. */
export function businessStamp(instant: Date | string): string {
  const { day, month, hours, minutes } = businessClock(instant);
  return `${day} ${MONTHS[month - 1]}, ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** The calendar date in Vancouver at `instant` (now by default), as `YYYY-MM-DD`. */
export function businessDate(instant: Date | string = new Date()): string {
  const { year, month, day } = businessClock(instant);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
